import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";

const ANIMATION_DURATION_MS = 1200;

const focusPulse = keyframes`
  0% { background-color: rgba(124, 58, 237, 0.08); }
  50% { background-color: rgba(124, 58, 237, 0.06); }
  100% { background-color: #ffffff; }
`;

const horizontalShine = keyframes`
  0% { left: -40%; opacity: 0; }
  10% { opacity: 1; }
  100% { left: 110%; opacity: 0; }
`;

/**
 * A container that plays the Vero-branded purple flash + horizontal shine animation.
 * Used for loading states (e.g., custom doc creation) and focus indicators (e.g., VeroGo toggle).
 *
 * When `flash` prop is true, the animation plays once and stops.
 * When `loop` prop is true, the animation loops continuously (for loading states).
 */
export const FlashContainer = styled.div<{ flash?: boolean; loop?: boolean }>(
  (props) => ({
    position: "relative" as const,
    overflow: "hidden",
    backgroundColor: "#ffffff",
    transition: "background-color 320ms ease",
    willChange: "background-color",
    ...(props.flash || props.loop
      ? {
          animation: `${focusPulse} ${ANIMATION_DURATION_MS}ms ease-out ${
            props.loop ? "infinite" : "forwards"
          }`,
          "&::after": {
            content: '""',
            position: "absolute" as const,
            top: 0,
            left: "-40%",
            width: "40%",
            height: "100%",
            pointerEvents: "none" as const,
            background:
              "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.5), transparent)",
            animation: `${horizontalShine} ${ANIMATION_DURATION_MS}ms cubic-bezier(0.22, 1, 0.36, 1) ${
              props.loop ? "infinite" : ""
            }`,
          },
        }
      : {}),
  })
);

/**
 * A grid row variant of FlashContainer for use in table/grid layouts.
 * Accepts a `columns` prop for CSS grid-template-columns.
 */
export const FlashRow = styled(FlashContainer)<{ columns?: string }>((props) => ({
  display: "grid",
  gridTemplateColumns: props.columns || "1fr",
  alignItems: "center",
  padding: "10px 16px",
  minHeight: "48px",
}));
